import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { classSet } from '@umu/umu-dom';
import { getData, findLastIndex } from '@umu/umu-util';
import { transformPx2Rem, getRootScale } from 'businessCommon/src/@utils/fontSize';
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized/dist/es/CellMeasurer';
import { List } from 'react-virtualized/dist/es/List';

import {
    getInitWidth,
    getItemStyle,
    getItemClassName,
    parseHeaderRows,
    flatColumns,
    flatColumnsFirstLevel,
} from '../util';

import {
    IData,
    FixedDirection,
    ICalculatedColumn,
    ScrollProcess,
    IProps,
    TheadRowItem,
    FlatColumnsFirstLevel,
} from '../interface';
import { useDebounce } from '@umu/umu-react-hooks';
import { useSelection } from '../hooks';
import If from '../../if';
import './customTable.scss';

const DELAY_SCROLL = 10;
/**
 * 表格，支持自定义 td、固定列
 * @author Kody
 */
export const CustomTable = (props: IProps) => {
    const {
        columns: originColumns = [],
        data = [],
        width = 0,
        blank = '',
        onColumnClick,
        refTable,
        scrollNumber,
        children,
        className = '',
        miniNumTdWidth = 100,
        isMultilevel = false,
        rowSelection,
        isTransformPx2Rem = true,
    } = props;
    const [columns] = useSelection(
        {
            columns: originColumns,
            data,
        },
        rowSelection,
    );
    const minimumTdWidth = miniNumTdWidth; // td 最小宽度，width 低于此宽度不生效
    const [calculatedColumns, setCalculatedColumns] = useState<ICalculatedColumn[]>(columns);
    const [scrollProcess, setScrollProcess] = useState<ScrollProcess | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const lWidth = columns.length * minimumTdWidth;
    const initWidth = useMemo(() => getInitWidth(columns), [columns]); // 初始化宽度
    const [minWidth, setMinWidth] = useState(initWidth ? initWidth : lWidth > width ? lWidth : width);

    const lastFixedLeft = useMemo(() => {
        return findLastIndex(columns, (v) => v.fixed === FixedDirection.left);
    }, [columns]);
    const firstFixedRight = useMemo(() => {
        return columns.findIndex((v) => v.fixed === FixedDirection.right);
    }, [columns]);
    const hasFixed = useMemo(() => !!(lastFixedLeft || firstFixedRight), [lastFixedLeft, firstFixedRight]);

    const columnClickHandle = useCallback(
        (item: IData, dataIndex: string) => {
            return () => {
                if (typeof onColumnClick === 'function') {
                    onColumnClick(item, dataIndex);
                }
            };
        },
        [onColumnClick],
    );

    /**
     * 监听容器滚动，显隐 fixed 列分割线
     */
    useEffect(() => {
        const node = containerRef.current;
        if (node && hasFixed) {
            if (node.scrollWidth > node.offsetWidth) {
                setScrollProcess(ScrollProcess.start);
            }
            const scrollHandle = () => {
                const scrollLeft = node.scrollLeft;
                if (node.scrollLeft === 0) {
                    setScrollProcess(ScrollProcess.start);
                } else if (scrollLeft === node.scrollWidth - node.offsetWidth) {
                    setScrollProcess(ScrollProcess.end);
                } else {
                    setScrollProcess(ScrollProcess.process);
                }
            };
            node.addEventListener('scroll', scrollHandle);
            return () => {
                node.removeEventListener('scroll', scrollHandle);
            };
        }
        // tslint:disable-next-line: no-unused-expression
        return () => {
            return null;
        };
    }, [width, hasFixed]);
    // 滚动条交互监听
    useEffect(() => {
        if (refTable && refTable.current && containerRef.current) {
            containerRef.current.scrollLeft = scrollNumber || 0;
        }
    }, [scrollNumber]);
    useEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return;
        }
        const fixedRight = node.querySelectorAll('.fixed-right');
        const fixedLeft = node.querySelectorAll('.fixed-left');
        for (let i = 0, len = fixedRight.length; i < len; i++) {
            const ele: any = fixedRight[i];
            if (ele.height) {
                ele.height = ele.parentElement!.offsetHeight - 1;
            }
        }
        for (let i = 0, len = fixedLeft.length; i < len; i++) {
            const ele: any = fixedLeft[i];
            if (ele.height) {
                ele.height = ele.parentElement!.offsetHeight - 1;
            }
        }
    }, [width, data, columns]);

    const computedColumnsWidth = (tableWidth: number) => {
        let existWidth = 0;
        let hasWidthSetNum = 0;
        let countWidth = 0;
        columns.forEach((v) => {
            if (v.width && v.width !== 'auto') {
                hasWidthSetNum++;
                const actionWidth =
                    typeof v.width === 'number'
                        ? v.width
                        : /%$/.test(v.width)
                        ? Math.round((parseFloat(v.width) / 100) * tableWidth)
                        : parseFloat(v.width);
                v.width = actionWidth;
                existWidth += actionWidth;
                countWidth += actionWidth;
            } else {
                countWidth += minimumTdWidth;
            }
        });
        return {
            existWidth,
            hasWidthSetNum,
            countWidth,
        };
    };
    /**
     * 在此计算宽度与位置
     */
    const tableRef = useCallback(
        (node: HTMLTableElement) => {
            if (node) {
                // 修复：将DOM实际宽度转换回基准值，避免字体缩放导致的累积问题
                const actualOffsetWidth = isTransformPx2Rem ? node.offsetWidth / getRootScale() : node.offsetWidth;
                const tableWidth = initWidth ? initWidth : minWidth > actualOffsetWidth ? minWidth : actualOffsetWidth;
                const { existWidth, hasWidthSetNum, countWidth } = computedColumnsWidth(tableWidth);

                let leftPosition = 0;
                const l = columns.length;
                const remainderNum = l - hasWidthSetNum;
                const remainderItemWidth = (tableWidth - existWidth) / remainderNum;
                const newColumns = columns.map((v, index) => {
                    const itemWidth = typeof v.width === 'number' ? v.width : remainderItemWidth;
                    const newColumn: ICalculatedColumn = Object.assign({}, v, { width: itemWidth });
                    if (v.fixed === FixedDirection.left) {
                        newColumn.position = leftPosition;
                    } else if (v.fixed === FixedDirection.right) {
                        const isLast = index === l - 1;
                        newColumn.position = isLast ? 0 : tableWidth - leftPosition - itemWidth;
                    }
                    leftPosition += itemWidth;
                    return newColumn;
                });

                setMinWidth(countWidth);
                setCalculatedColumns(newColumns);
            }
        },
        [columns, minWidth, initWidth, isTransformPx2Rem],
    );

    const renderThs = () => {
        return calculatedColumns.map((column, index) => {
            const style = getItemStyle(column);
            const className = getItemClassName(column, index, lastFixedLeft, firstFixedRight);
            const title = column.title || (typeof column.name === 'string' ? column.name : '');
            return (
                <th key={column.dataIndex || index} className={className} title={title} style={style}>
                    {column.name}
                </th>
            );
        });
    };

    const renderMultistepTableTheadThs = (theadThs: TheadRowItem[], firstChildrenCountMap: FlatColumnsFirstLevel) => {
        return theadThs.map((theadTh, index) => {
            const { column } = theadTh;
            const style = getItemStyle(column, firstChildrenCountMap[column.dataIndex]?.length);

            const className = getItemClassName(column, index, lastFixedLeft, firstFixedRight);
            const title = column.title || (typeof column.name === 'string' ? column.name : '');
            return (
                <th
                    rowSpan={theadTh.rowSpan || 1}
                    colSpan={theadTh.colSpan || 1}
                    key={column.dataIndex || index}
                    className={className}
                    title={title}
                    style={style}
                >
                    {column.name}
                </th>
            );
        });
    };

    const renderMultistepTableThead = () => {
        const thsRows = parseHeaderRows(calculatedColumns);
        const firstChildrenCountMap = flatColumnsFirstLevel(calculatedColumns);

        return (
            <thead>
                {thsRows.map((thRow, index) => {
                    return <tr key={index}>{renderMultistepTableTheadThs(thRow, firstChildrenCountMap)}</tr>;
                })}
            </thead>
        );
    };

    const renderTd = (item: IData, rowIndex: number) => {
        const tds = isMultilevel ? flatColumns(calculatedColumns) : calculatedColumns;

        return tds.map((column, index) => {
            const style = getItemStyle(column, 0, isTransformPx2Rem);
            const key = `${column.dataIndex} ${index}`;
            const className = getItemClassName(column, index, lastFixedLeft, firstFixedRight);

            if (typeof column.render === 'function') {
                return (
                    <td
                        key={key}
                        style={style}
                        className={className}
                        onClick={columnClickHandle(item, column.dataIndex)}
                    >
                        {column.render(item, rowIndex, index) || ''}
                    </td>
                );
            }
            const params = [item, ...column.dataIndex.split('.')];
            // @ts-ignore
            const tdContent = getData.apply(null, params as any) || '';
            return (
                <td key={key} style={style} className={className} onClick={columnClickHandle(item, column.dataIndex)}>
                    {tdContent as string}
                </td>
            );
        });
    };

    const renderPlaceholder = useMemo(() => {
        if (!blank) {
            return null;
        }
        return (
            <tr>
                <td className="blank" colSpan={columns.length}>
                    {blank}
                </td>
            </tr>
        );
    }, [blank]);

    const renderTr = () => {
        if (blank) {
            return renderPlaceholder;
        } else {
            return data.map((item, i) => {
                return <tr key={i}>{renderTd(item, i)}</tr>;
            });
        }
    };

    const onScrollCapture = useDebounce(() => {
        if (refTable && refTable.current && containerRef.current) {
            refTable.current.scrollLeft = containerRef.current.scrollLeft;
        }
    }, DELAY_SCROLL);

    const cache = useMemo(() => {
        return new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 300,
        });
    }, []);

    function virtualizedRowRenderer({
        key, // Unique key within array of rows
        index, // Index of row within collection
        style,
        parent,
    }: any) {
        return (
            <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
                {({ measure, registerChild }: any) => {
                    // 首页的测量不准确，所以这里做了一个延迟，原因可能是因为首次渲染的时候，dom还没有渲染完成
                    setTimeout(() => {
                        if (index < 20) {
                            try {
                                measure();
                            } catch (e) {}
                        }
                    }, 0);
                    return (
                        <tr key={key} ref={registerChild as any} style={style}>
                            {renderTd(data[index], index)}
                        </tr>
                    );
                }}
            </CellMeasurer>
        );
    }

    const renderTbody = () => {
        if (!data || data.length === 0) {
            return <tbody>{renderPlaceholder}</tbody>;
        }
        return (
            <>
                <If condition={!!props.isVirtualized}>
                    <tbody>
                        <List
                            deferredMeasurementCache={cache}
                            rowHeight={cache.rowHeight}
                            width={props.virtualizedListProps?.width || 730}
                            height={props.virtualizedListProps?.height || 340}
                            rowCount={data.length}
                            rowRenderer={virtualizedRowRenderer}
                        ></List>
                    </tbody>
                </If>
                <If condition={!props.isVirtualized}>
                    <tbody>{renderTr()}</tbody>
                </If>
            </>
        );
    };

    const render = () => {
        const styleMinWidth = initWidth ? initWidth : minWidth + 'px';
        const style = {
            minWidth: isTransformPx2Rem ? transformPx2Rem(styleMinWidth) : styleMinWidth,
            width: initWidth ? initWidth : width ? width + 'px' : '100%',
        };
        const containerClassName = classSet({
            'custom-table': true,
            'scroll-start': scrollProcess === ScrollProcess.start,
            'scroll-end': scrollProcess === ScrollProcess.end,
            'scroll-process': scrollProcess === ScrollProcess.process,
        });
        return (
            <div className={`${containerClassName} ${className}`}>
                <div className="table-wrap" ref={containerRef} onScrollCapture={onScrollCapture}>
                    <table style={style} ref={tableRef}>
                        {isMultilevel ? (
                            renderMultistepTableThead()
                        ) : (
                            <thead>
                                <tr>{renderThs()}</tr>
                            </thead>
                        )}
                        {renderTbody()}
                        {children}
                    </table>
                </div>
            </div>
        );
    };
    return render();
};
